import React from 'react'
import {FormattedNumber} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Box} from '@vio/atlas/helpers/Box'
import ContentWrapper from '@vio/atlas/helpers/ContentWrapper'
import {Flex} from '@vio/atlas/helpers/Flex'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import minus from '@vio/atlas/utils/minus'

import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {calculateNightlyRate} from '@daedalus/core/src/offer/business/offersMapping'
import {
  getDisplayPrice,
  getTotalPrice
} from '@daedalus/core/src/price/business/price'

import {UnifiedDateTime} from '../../../utils/UnifiedDateTime'
import {
  getFreezeSelectedContext,
  getOfferEligibility
} from '../../modules/selector'
import {getDayDistance} from '../../utils/date'

interface Props {
  shouldShowPayNow: boolean
}

const FirstRow = styled.div`
  display: flex;
  flex-direction: row;
  border-top: 1px solid ${cssTheme.colors.border.neutral.c100};
`

const FirstRowCell = styled.div`
  flex: 1;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};

  &:last-child {
    border-left: 1px solid ${cssTheme.colors.border.neutral.c100};
    display: flex;
    flex-direction: column;
    align-items: end;
  }
`

const TotalPriceRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  border-top: 1px solid ${cssTheme.colors.border.neutral.c100};

  > div {
    display: flex;
  }
`

const SecondRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  border-top: 1px solid ${cssTheme.colors.border.neutral.c100};
`

const PayNow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ExpireDate = styled(Text)`
  border-bottom: ${cssTheme.layout.spacing.s100} dotted
    ${cssTheme.colors.border.neutral.c600};
  padding-bottom: 1px;
  &:hover,
  &:focus {
    text-decoration: none;
    border-bottom: ${cssTheme.layout.spacing.s100} solid
      ${cssTheme.colors.border.brand.c500};
    color: ${cssTheme.colors.content.brand.c500};
  }
`

const StyledTooltip = styled(Tooltip)`
  width: calc(100% - ${cssTheme.layout.spacing.s500});
  margin-left: ${minus(cssTheme.layout.spacing.s200)};
  cursor: default !important;
`

export const PriceSection = ({shouldShowPayNow}: Props) => {
  const {response: offerCheckResponse, isLoading} =
    useSelector(getOfferEligibility)
  const {searchDetail} = useSelector(getFreezeSelectedContext)

  if (isLoading || !offerCheckResponse?.prices) return null

  const {checkIn, checkOut} = searchDetail
  const expireAt =
    offerCheckResponse?.expiresAt && new Date(offerCheckResponse?.expiresAt)

  const displayPrice = getDisplayPrice(offerCheckResponse?.prices)

  const numberOfNights = getDayDistance(checkIn, checkOut)

  const freezeTotalPrice = getTotalPrice(displayPrice)
  const freezePricePerNight =
    freezeTotalPrice &&
    calculateNightlyRate(freezeTotalPrice, checkIn, checkOut)

  const displayPriceCurrency = displayPrice?.chargeable?.currency as string

  const formattedNightlyPrice = (
    <FormattedNumber
      value={freezePricePerNight}
      currency={displayPriceCurrency}
      style="currency"
    />
  )

  const freezeExpires = (
    <UnifiedDateTime value={expireAt} format={'date-long'} />
  )

  const toolTipContent = (
    <FormattedMessageWrapper
      id="dealFreeze.tooltipContent"
      defaultMessage="Room price and availability guaranteed until <br></br> {timeAndDate}."
      values={{
        timeAndDate: (
          <UnifiedDateTime
            value={expireAt}
            format={'timefirst-longform-weekday'}
          />
        ),
        br: () => <br />
      }}
    />
  )

  return (
    <>
      <FirstRow>
        <FirstRowCell>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper
              id="dealFreeze.holdingRoomUntil"
              defaultMessage="Holding room until"
            />
          </Text>
          <StyledTooltip
            content={toolTipContent}
            placement={TooltipPlacement.TopStart}
          >
            <ExpireDate variant="titleM">{freezeExpires}</ExpireDate>
          </StyledTooltip>
        </FirstRowCell>
        <FirstRowCell>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper
              id="dealFreeze.guaranteedPrice"
              defaultMessage="Guaranteed price"
            />
          </Text>
          {freezePricePerNight && (
            <ContentWrapper paddingTop="s100">
              <Flex.Row alignItems="center" gap="s100">
                <Text variant="labelM">{formattedNightlyPrice}</Text>
                <Text variant="bodyS">
                  <FormattedMessageWrapper
                    id="perNight"
                    defaultMessage="/ night"
                  />
                </Text>
              </Flex.Row>
            </ContentWrapper>
          )}
        </FirstRowCell>
      </FirstRow>
      {freezeTotalPrice && (
        <TotalPriceRow>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              <Text variant="titleM">
                <FormattedMessageWrapper
                  id="dealFreeze.totalPriceOfStay"
                  defaultMessage="Total price of stay:"
                />
              </Text>
              <Text variant="bodyS" colorPath="content.neutral.c600">
                <FormattedMessageWrapper
                  id="dealFreeze.priceBreakdown"
                  defaultMessage="({price} x {numberOfNights, plural, one {1 night} other {# nights}})"
                  values={{
                    numberOfNights,
                    price: formattedNightlyPrice
                  }}
                />
              </Text>
            </Box>
            <Text variant="labelM">
              <FormattedNumber
                value={freezeTotalPrice}
                currency={displayPriceCurrency}
                style="currency"
              />
            </Text>
          </Box>
        </TotalPriceRow>
      )}
      {shouldShowPayNow && (
        <SecondRow>
          <PayNow>
            <Text variant="titleM">
              <FormattedMessageWrapper
                id="dealFreeze.costToFreeze"
                defaultMessage="Cost to freeze:"
              />
            </Text>
            <Text variant="titleL" colorPath="content.special.c600">
              <FormattedNumber
                value={0}
                currency={displayPriceCurrency}
                style="currency"
              />
            </Text>
          </PayNow>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper
              id="dealFreeze.noPaymentRequired"
              defaultMessage="Secure up to five deals at once – totally free"
            />
          </Text>
        </SecondRow>
      )}
    </>
  )
}
